import React, { useEffect, useState } from 'react';
import { bool, func, number, string } from 'prop-types';
import { Form as FinalForm, FormSpy } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { PURCHASE_PROCESS_NAME } from '../../../transactions/transaction';

import { numberAtLeast } from '../../../util/validators';

import { Form, PrimaryButton, H6, NamedLink, FieldTextInput, FieldSelect } from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import Attachments from '../Attachments/Attachments';
import CollectionAndShipping from '../CollectionAndShipping/CollectionAndShipping';
import JobLocation from '../JobLocation/JobLocation';

import css from './ProductOrderForm.module.css';

const MAX_QUANTITY_FOR_DROPDOWN = 100;

const getAttachments = (attachments, keys, values) => {
  return attachments
    .filter(attachment => keys.includes(attachment.id))
    .map(attachment => ({
      attachment: attachment.id,
      quantity: values[`${attachment.id}_quantity`] ?? 1,
    }));
};

// Browsers can't render huge number of select options.
// (stock is shown inside select element)
// Note: input element could allow ordering bigger quantities
// const MAX_QUANTITY_FOR_DROPDOWN = 100;

const handleFetchLineItems = ({
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
  freeAttachments,
  paidAttachments,
  jobLocation,
  transportation,
  author,
  quantity = 1,
}) => {
  const isBrowser = typeof window !== 'undefined';
  const stockReservationQuantity = Number.parseInt(quantity, 10);

  // TODO: sometimes location field returns invalid format of location
  // if location is ok then ignore fetchLineItemsInProgress
  if (
    isBrowser &&
    transportation &&
    (!!jobLocation?.selectedPlace || !fetchLineItemsInProgress)
  ) {

    onFetchTransactionLineItems({
      orderData: {
        deliveryMethod: transportation,
        stockReservationQuantity,
        freeAttachments,
        paidAttachments,
        location: jobLocation?.search ? jobLocation.search : null,
        transportation,
        providerId: author.id.uuid
      },
      listingId,
      isOwnListing,
    });
  }
};

const renderForm = formRenderProps => {
  const [mounted, setMounted] = useState(false);
  const {
    // FormRenderProps from final-form
    handleSubmit,
    currentStock,
    // Custom props passed to the form component
    intl,
    listingId,
    isOwnListing,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    authorStripeConnectedAccount,
    price,
    marketplaceName,
    values,
    isSeller,
    isBuyer,
    isVerified,
    currency,
    isIndividual,
    listing,
    form,
    author
  } = formRenderProps;

  const listingTitle = listing?.attributes?.title;

  // Note: don't add custom logic before useEffect
  useEffect(() => {
    setMounted(true);

    // Side-effect: fetch line-items after mounting if possible
    // const { quantity, deliveryMethod } = values;
    if (!formRenderProps.hasMultipleDeliveryMethods) {
      handleFetchLineItems({
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
        author,
        quantity: values.quantity,
      });
    }
  }, []);

  // If form values change, update line-items for the order breakdown
  const handleOnChange = formValues => {
    const { collectionOrShipping, jobLocation, quantity } = formValues.values;

    const freeAttachments = listing?.attributes?.publicData?.freeAttachments ?? [];
    const paidAttachments = listing?.attributes?.publicData?.paidAttachments ?? [];
    const keys = Object.keys(formValues.values).filter(attach => formValues.values[attach]?.length);

    if (mounted) {
      handleFetchLineItems({
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
        transportation: collectionOrShipping,
        jobLocation: collectionOrShipping === "shipping" ? jobLocation : null,
        paidAttachments: getAttachments(paidAttachments, keys, formValues.values),
        freeAttachments: getAttachments(freeAttachments, keys, formValues.values),
        author,
        quantity,
      });
    }
  };

  // In case quantity and deliveryMethod are missing focus on that select-input.
  // Otherwise continue with the default handleSubmit function.
  const handleFormSubmit = e => {
    const { collectionOrShipping } = values || {};
    if (collectionOrShipping) {
      handleSubmit(e);
    } else {
      e.preventDefault();
    }
  };

  const breakdownData = {};
  const showBreakdown =
    breakdownData && lineItems && !fetchLineItemsError;

  const submitInProgress = fetchLineItemsInProgress;
  const submitDisabled =
    isSeller ||
    (isBuyer && !isVerified) ||
    !authorStripeConnectedAccount ||
    !values.collectionOrShipping;

  const quantityRequiredMsg = intl.formatMessage({ id: 'ProductOrderForm.quantityRequired' });

  const hasStock = currentStock && currentStock > 0;
  const selectableStock =
    currentStock > MAX_QUANTITY_FOR_DROPDOWN ? MAX_QUANTITY_FOR_DROPDOWN : currentStock;
  const quantities = hasStock ? [...Array(selectableStock).keys()].map(i => i + 1) : [];
  const hasNoStockLeft = typeof currentStock != null && currentStock === 0;
  const hasOneItemLeft = typeof currentStock != null && currentStock === 1;

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormSpy subscription={{ values: true }} onChange={handleOnChange} />

      {hasNoStockLeft ? null : hasOneItemLeft ? (
        <FieldTextInput
          id="quantity"
          className={css.quantityField}
          name="quantity"
          type="hidden"
          validate={numberAtLeast(quantityRequiredMsg, 1)}
        />
      ) : (
        <FieldSelect
          id="quantity"
          className={css.quantityField}
          name="quantity"
          disabled={!hasStock}
          label={intl.formatMessage({ id: 'ProductOrderForm.quantityLabel' })}
          validate={numberAtLeast(quantityRequiredMsg, 1)}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'ProductOrderForm.selectQuantityOption' })}
          </option>
          {quantities.map(quantity => (
            <option key={quantity} value={quantity}>
              {intl.formatMessage({ id: 'ProductOrderForm.quantityOption' }, { quantity })}
            </option>
          ))}
        </FieldSelect>
      )}

      <CollectionAndShipping values={values} intl={intl} listing={listing} formApi={form} />

      <JobLocation values={values} intl={intl} />

      <Attachments values={values} intl={intl} listing={listing} formApi={form} />

      {showBreakdown ? (
        <div className={css.breakdownWrapper}>
          <H6 as="h3" className={css.bookingBreakdownTitle}>
            <FormattedMessage id="ProductOrderForm.breakdownTitle" />
          </H6>
          <hr className={css.totalDivider} />
          <EstimatedCustomerBreakdownMaybe
            breakdownData={breakdownData}
            lineItems={lineItems}
            currency={price?.currency || currency}
            marketplaceName={marketplaceName}
            processName={PURCHASE_PROCESS_NAME}
            listingTitle={listingTitle}
          />
        </div>
      ) : null}

      <div className={css.submitButton}>
        <PrimaryButton
          type={submitDisabled ? 'button' : 'submit'}
          inProgress={submitInProgress}
          disabled={submitDisabled}
        >
          <FormattedMessage id="ProductOrderForm.ctaButton" />
        </PrimaryButton>
      </div>
      {isSeller ? (
        <p className={css.finePrint}>
          <FormattedMessage id="BookingDatesForm.sellerNotification" />
        </p>
      ) : !authorStripeConnectedAccount ? (
        <p className={css.finePrint}>
          <FormattedMessage id="BookingForm.authorNoStripeConnectedAccountMessage" />
        </p>
      ) : null}
      {isBuyer && !isVerified ? (
        <p className={css.finePrint}>
          <FormattedMessage
            id="BookingDatesForm.unverifiedBuyer"
            values={{
              link: (
                <NamedLink name={isIndividual ? 'IDVerificationPage' : 'StripePayoutPage'}>
                  <FormattedMessage id="BookingDatesForm.linkLabel" />
                </NamedLink>
              ),
            }}
          />
        </p>
      ) : null}
    </Form>
  );
};

const ProductOrderForm = props => {
  const intl = useIntl();
  const { currentStock, pickupEnabled, shippingEnabled, showListingInProgress } = props;

  // Should not happen for listings that go through EditListingWizard.
  // However, this might happen for imported listings.
  if (!pickupEnabled && !shippingEnabled && !showListingInProgress) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProductOrderForm.noDeliveryMethodSet" />
      </p>
    );
  }

  const hasOneItemLeft = currentStock && currentStock === 1;
  const quantityMaybe = hasOneItemLeft ? { quantity: '1' } : {};
  const singleDeliveryMethodAvailableMaybe =
    shippingEnabled && !pickupEnabled
      ? { deliveryMethod: 'shipping' }
      : !shippingEnabled && pickupEnabled
      ? { deliveryMethod: 'pickup' }
      : {};
  const hasMultipleDeliveryMethods = pickupEnabled && shippingEnabled;
  const initialValues = { ...quantityMaybe, ...singleDeliveryMethodAvailableMaybe };

  return (
    <FinalForm
      initialValues={initialValues}
      hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
      {...props}
      intl={intl}
      render={renderForm}
    />
  );
};

ProductOrderForm.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  currentStock: null,
  listingId: null,
  isOwnListing: false,
  lineItems: null,
  fetchLineItemsError: null,
};

ProductOrderForm.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,

  // form
  formId: string.isRequired,
  onSubmit: func.isRequired,

  // listing
  listingId: propTypes.uuid,
  price: propTypes.money,
  currentStock: number,
  isOwnListing: bool,

  // line items
  lineItems: propTypes.lineItems,
  onFetchTransactionLineItems: func.isRequired,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // other
  onContactUser: func,
};

export default ProductOrderForm;
